import { PaymentForm } from "../../components/Payments/PaymentForm";
import { useState, useEffect } from "react";
import { useImplicitAuth } from "../../azureLogin/useImplicitAuth";
import ImplicitAuthContext from "../../azureLogin/useImplicitAuth";
import PPHeader from "../../components/Payments/PPHeader/PPHeader";
import Footer from "../../components/cgfns/cgfnsFooter/cgfnsFooter";
import PayerOrdersView from "../../components/Payments/PayerOrdersView";
import styles from "../../components/Payments/paymentForm.module.css";

const DUE_DILIGENCE_MESSAGE =
  "Thank you for your order. We appreciate your patience while we conduct necessary due diligence to comply with U.S. legal standards and internal policies and procedures for payment processing. No further action is required from you at this time unless our team contacts you for additional information.";

const TPP_STATUS = {
  Checking: "checking",
  Allowed: "allowed",
  Blocked: "blocked",
};

const PageShell = ({ children, hideNav = false, centered = false }) => (
  <div className={styles.paymentsShell}>
    <PPHeader hideNav={hideNav} />
    <div className={`${styles.paymentsContent} ${centered ? styles.centeredContent : ""}`}>{children}</div>
    <Footer />
  </div>
);

const StatusCard = ({ text }) => (
  <PageShell>
    <h4 style={{ backgroundColor: "white", padding: "30px", height: 550, width: "100%" }}>{text}</h4>
  </PageShell>
);

const DueDiligenceNotice = () => (
  <div className={styles.dueDiligenceCard}>
    <p className={styles.dueDiligenceMessage}>{DUE_DILIGENCE_MESSAGE}</p>
  </div>
);

const sameId = (left, right) =>
  left != null && right != null && String(left) !== "" && String(left) === String(right);

const isTokenOrder = (item, tokenOrderId, loadedOrder) =>
  [tokenOrderId, loadedOrder?.orderNumber, loadedOrder?.orderHeadersId].some(
    (id) => sameId(id, item?.orderNumber) || sameId(id, item?.orderHeadersId)
  );

const PaymentsHome = () => {
  const { fetchData, fetchOrdersData, jwt } = useImplicitAuth();
  const [message, setMessage] = useState("Loading...");
  const [order, setOrder] = useState({});
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [tokenOrderId, setTokenOrderId] = useState(null);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [orderLoaded, setOrderLoaded] = useState(false);
  const [tppStatus, setTppStatus] = useState(TPP_STATUS.Checking);

  useEffect(() => {
    const verifyTppEmail = async () => {
      try {
        const thirdPartyPayerEmail = jwt()?.authEmail;

        if (!thirdPartyPayerEmail) {
          setTppStatus(TPP_STATUS.Allowed);
          return;
        }

        const response = await fetchData('/comply/tppprecheckV1', {
          method: 'POST',
          body: JSON.stringify({ email: thirdPartyPayerEmail }),
        });

        if (!response?.ok) {
          setTppStatus(TPP_STATUS.Allowed);
          return;
        }

        const verification = await response.json();
        const allowed =
          verification?.allowed === true ||
          String(verification?.allowed ?? '').trim().toUpperCase() === 'Y';
        console.log('TPP precheck allowed:', allowed);

        setTppStatus(allowed ? TPP_STATUS.Allowed : TPP_STATUS.Blocked);
      } catch (error) {
        console.error("Error verifying third-party payer email:", error);
        setTppStatus(TPP_STATUS.Allowed);
      }
    };

    verifyTppEmail();
  }, []);


  useEffect(() => {
    const fetchOrdersCount = async () => {
      try {
        const response = await fetchOrdersData('/federated/Application/payer/me');

        console.log('Response:', response);

        if (!response || !response.data) {
          throw new Error("No data received from the API");
        }

        const filteredData = response.data.filter(item => item.lineBalance > 0);
        setUnpaidOrders(filteredData);
        console.log('Total number of orders:', filteredData.length);

        if (filteredData.length == 0) {
          setMessage("No orders found.");
        }
      } catch (error) {
        console.error("Error fetching federated data:", error);
        setMessage("Token has expired.");
      } finally {
        setOrdersLoaded(true);
      }
    };

    fetchOrdersCount();
  }, []);

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const orderId = jwt()?.extension_OrderId;
        setTokenOrderId(orderId ?? null);

        if (!orderId) {
          setMessage("Token has expired.");
          return;
        }

        const response = await fetchData(`/federated/Application/${orderId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch order data. Status: ${response.status}`);
        }

        const orderData = await response.json();
        orderData.lineBalance = orderData.lineBalance;
        setOrder(orderData);
        console.log("Order Record", orderData);
      } catch (error) {
        console.error("Error fetching federated data:", error);

        if (error.status === 401) {
          setMessage("Token has expired.");
        }
        setMessage("Token has expired.");
      } finally {
        setOrderLoaded(true);
      }
    };

    fetchOrderData();
  }, []);

  if (tppStatus === TPP_STATUS.Checking) {
    return <StatusCard text="Loading..." />;
  }

  if (tppStatus === TPP_STATUS.Blocked) {
    return (
      <PageShell hideNav centered>
        <DueDiligenceNotice />
      </PageShell>
    );
  }

  if (!ordersLoaded || !orderLoaded) {
    return <StatusCard text="Loading..." />;
  }

  const soleUnpaid = unpaidOrders.length === 1 ? unpaidOrders[0] : null;
  const showSingleOrderForm =
    soleUnpaid &&
    isTokenOrder(soleUnpaid, tokenOrderId, order) &&
    Number(soleUnpaid.lineBalance) > 0 &&
    Number(order?.lineBalance) > 0;

  if (showSingleOrderForm) {
    return (
      <PageShell>
        <PaymentForm
          order={order}
          onBlocked={() => setTppStatus(TPP_STATUS.Blocked)}
        />
      </PageShell>
    );
  }

  if (unpaidOrders.length >= 1) {
    return (
      <PayerOrdersView
        emailAlreadyVerified
        onBlocked={() => setTppStatus(TPP_STATUS.Blocked)}
      />
    );
  }

  return <StatusCard text={message} />;
};

const PaymentsSite = () => {
  return (
    <ImplicitAuthContext>
      <PaymentsHome />
    </ImplicitAuthContext>
  );
};

export default PaymentsSite;
